import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiSettings } = FiIcons;

function CookieBanner({ onConsent }) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be changed
    analytics: false,
    advertising: false
  });

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      advertising: true
    };
    onConsent(consent);
  };

  const handleDeclineAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      advertising: false
    };
    onConsent(consent);
  };

  const handleSavePreferences = () => {
    onConsent(preferences);
  };

  const handlePreferenceChange = (key, value) => {
    if (key === 'necessary') return; // Cannot change necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {t('cookies.title')}
            </h2>
          </div>

          <p className="text-gray-600 mb-6">
            {t('cookies.description')}
          </p>

          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4 mb-6"
              >
                {/* Necessary Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {t('cookies.necessary.title')}
                    </h3>
                    <div className="bg-gray-100 rounded-full p-1">
                      <div className="bg-green-500 w-4 h-4 rounded-full"></div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookies.necessary.description')}
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {t('cookies.analytics.title')}
                    </h3>
                    <button
                      onClick={() => handlePreferenceChange('analytics', !preferences.analytics)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preferences.analytics ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookies.analytics.description')}
                  </p>
                </div>

                {/* Advertising Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {t('cookies.advertising.title')}
                    </h3>
                    <button
                      onClick={() => handlePreferenceChange('advertising', !preferences.advertising)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preferences.advertising ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.advertising ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t('cookies.advertising.description')}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAcceptAll}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              {t('cookies.acceptAll')}
            </button>
            
            <button
              onClick={handleDeclineAll}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              {t('cookies.declineAll')}
            </button>
            
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              <SafeIcon icon={FiSettings} className="mr-2" />
              {t('cookies.customize')}
            </button>
          </div>

          {showDetails && (
            <div className="mt-4">
              <button
                onClick={handleSavePreferences}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                {t('cookies.savePreferences')}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default CookieBanner;